import { Alert } from 'antd';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ConfigurationCardAnchor } from './anchor/ConfigurationCardAnchor.tsx';
import { ConfigurationCardContextProvider } from './ConfigurationCardContext.tsx';
import { ConfigurationCardLoading } from './ConfigurationCardLoading.tsx';
import { ConfigurationCardDenseNav } from './dense/ConfigurationCardDenseNav.tsx';
import { ConfigurationCardSectionsMenu } from './dense/ConfigurationCardSectionsMenu.tsx';

const GroupLayout = styled.div`
  display: flex;
  gap: 12px;
`;

const CardsColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const AnchorColumn = styled.div`
  flex: 0 0 180px;
  align-self: stretch;
  max-width: 180px;
`;

interface IConfigurationCardGroupProps {
  children: React.ReactNode;
  /**
   * Vista densa: layout a colonna singola (senza Anchor), header compatti e
   * `description` come popover ⓘ. Pensata per i Drawer di configurazione.
   * @default false
   */
  dense?: boolean;
  /**
   * Rende collassabili le `ConfigurationCard` figlie (header cliccabile con
   * freccia di espansione). Le `ConfigurationSubCard` non sono collassabili.
   * Le card partono aperte.
   * @default false
   */
  collapsible?: boolean;
  /**
   * Primo caricamento (nessun dato): sostituisce le card con un placeholder a
   * spinner e nasconde la navigazione (Anchor o barra densa).
   */
  loading?: boolean;
  /**
   * Refetch con dati già presenti: overlay Spin sul contenuto delle card, che
   * restano visibili e navigabili.
   */
  fetching?: boolean;
  /**
   * Messaggio di errore mostrato inline al posto delle card, con navigazione
   * nascosta.
   */
  error?: string;
}

/**
 * Wrapper per un gruppo di ConfigurationCard con navigazione Anchor automatica.
 * Le card si registrano automaticamente e l'Anchor viene generato di conseguenza.
 * L'Anchor è sticky e si posiziona sotto l'header tramite l'AffixManager.
 *
 * In modalità `dense` il layout diventa a colonna singola e l'Anchor laterale
 * viene rimosso (nei Drawer non c'è spazio): la scannabilità è data dal
 * `collapse` delle sezioni invece che dalla navigazione Anchor.
 *
 * Gestisce gli stati asincroni: `error` → `loading` → contenuto (con overlay
 * `Spin` sulle card se `fetching`). Durante `loading`/`error` la navigazione
 * (Anchor o barra densa) è nascosta perché non ci sono card reali montate.
 */
export function ConfigurationCardGroup(props: IConfigurationCardGroupProps) {
  const {
    children,
    dense = false,
    collapsible = false,
    loading,
    fetching,
    error,
  } = props;
  const { t } = useTranslation();

  let content: React.ReactNode = children;
  if (error) {
    content = (
      <Alert
        type="error"
        showIcon
        message={t('card.network-responsive-card.error', 'Errore')}
        description={error}
      />
    );
  } else if (loading) {
    content = <ConfigurationCardLoading />;
  }

  // La navigazione non ha senso quando non ci sono card reali montate.
  const showNav = !loading && !error;

  return (
    <ConfigurationCardContextProvider
      dense={dense}
      collapsible={collapsible}
      fetching={fetching}
    >
      {dense ? (
        <>
          {showNav && (
            <>
              <ConfigurationCardDenseNav />
              <ConfigurationCardSectionsMenu />
            </>
          )}
          {content}
        </>
      ) : (
        <GroupLayout>
          <CardsColumn>{content}</CardsColumn>
          {showNav && (
            <AnchorColumn>
              <ConfigurationCardAnchor />
            </AnchorColumn>
          )}
        </GroupLayout>
      )}
    </ConfigurationCardContextProvider>
  );
}
